import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, MoreVert, Description, ArrowBack } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { contractsApi } from '../../services/benchmarkApi';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  margin: theme.spacing(1),
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: 8,
});

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  '& svg': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#F8F8F8',
    color: '#6B0F2A',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.8125rem',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(139, 159, 232, 0.08)',
  },
  '& .MuiDataGrid-row.Mui-selected': {
    backgroundColor: 'rgba(139, 159, 232, 0.16)',
    '&:hover': {
      backgroundColor: 'rgba(139, 159, 232, 0.24)',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius * 2,
    borderBottomRightRadius: theme.shape.borderRadius * 2,
  },
  '& .MuiTablePagination-root': {
    color: theme.palette.text.secondary,
  },
}));

const emptyForm = {
  contractNumber: '',
  contractName: '',
  contractType: '',
  clientContractor: '',
  originalContractValue: '',
  approvedChangeValue: '',
  revisedContractValue: '',
  currencyCode: '',
  startDate: '',
  finishDate: '',
  contractStatus: '',
};

const mapContractRow = (c) => ({
  id: c.contractId || c.id,
  projectId: c.projectId,
  contractNumber: c.contractNumber || '',
  contractName: c.contractName || '',
  contractType: c.contractType || '',
  clientContractor: c.clientContractor || '',
  originalContractValue:
    c.originalContractValue != null ? String(c.originalContractValue) : '',
  approvedChangeValue:
    c.approvedChangeValue != null ? String(c.approvedChangeValue) : '',
  revisedContractValue:
    c.revisedContractValue != null ? String(c.revisedContractValue) : '',
  currencyCode: c.currencyCode || '',
  startDate: c.startDate || '',
  finishDate: c.finishDate || '',
  contractStatus: c.contractStatus || '',
});

const BenchmarkContracts = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const location = useLocation();
  const project = location.state?.project;

  const [selectedRow, setSelectedRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const loadContracts = useCallback(async () => {
    if (!projectId) {
      setError('Project id is required');
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await contractsApi.list(projectId);
      setRows((res.data || []).map(mapContractRow));
    } catch (e) {
      setError(e.message || 'Failed to load contracts');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
  };

  const handleAdd = () => {
    setEditMode(false);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const handleEdit = () => {
    if (!selectedRow) {
      return;
    }
    setEditMode(true);
    setFormData({ ...selectedRow });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setFormData(emptyForm);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setError('');
    const payload = {
      projectId: Number(projectId),
      contractNumber: formData.contractNumber,
      contractName: formData.contractName,
      contractType: formData.contractType || null,
      clientContractor: formData.clientContractor,
      originalContractValue: formData.originalContractValue
        ? Number(formData.originalContractValue)
        : null,
      approvedChangeValue: formData.approvedChangeValue
        ? Number(formData.approvedChangeValue)
        : null,
      revisedContractValue: formData.revisedContractValue
        ? Number(formData.revisedContractValue)
        : null,
      currencyCode: formData.currencyCode || null,
      startDate: formData.startDate || null,
      finishDate: formData.finishDate || null,
      contractStatus: formData.contractStatus || null,
    };
    try {
      if (editMode && selectedRow) {
        await contractsApi.update(selectedRow.id, payload);
      } else {
        await contractsApi.create(payload);
      }
      handleCloseDialog();
      setSelectedRow(null);
      await loadContracts();
    } catch (e) {
      setError(e.message || 'Failed to save contract');
    }
  };

  const handleMenuClick = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedRowForMenu(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleMenuItemClick = (action) => {
    if (!selectedRowForMenu) return;
    const projectState = project || { id: Number(projectId) };
    const contractState = {
      ...selectedRowForMenu,
      projectId: selectedRowForMenu.projectId || Number(projectId),
    };

    switch (action) {
      case 'Estimate / BoQ':
        navigate(`/benchmark/estimated-budgets/${selectedRowForMenu.id}`, {
          state: { contract: contractState, project: projectState }
        });
        break;
      case 'Actual Cost':
        navigate(`/benchmark/actual-cost/${selectedRowForMenu.id}`, {
          state: { contract: contractState, project: projectState }
        });
        break;
      case 'Change Orders':
        navigate(`/benchmark/change-orders/${selectedRowForMenu.id}`, {
          state: { contract: contractState, project: projectState }
        });
        break;
      default:
        break;
    }
    
    handleMenuClose();
  };

  const columns = [
    {
      field: 'contractNumber',
      headerName: 'Contract Number',
      width: 150,
    },
    {
      field: 'contractName',
      headerName: 'Contract Name',
      width: 220,
      minWidth: 180,
    },
    {
      field: 'contractType',
      headerName: 'Type',
      width: 130,
    },
    {
      field: 'clientContractor',
      headerName: 'Contractor',
      width: 200,
      minWidth: 160,
    },
    {
      field: 'originalContractValue',
      headerName: 'Original Value',
      width: 140,
    },
    {
      field: 'approvedChangeValue',
      headerName: 'Approved CO',
      width: 130,
    },
    {
      field: 'revisedContractValue',
      headerName: 'Revised Value',
      width: 140,
    },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      width: 100,
    },
    {
      field: 'startDate',
      headerName: 'Start',
      width: 120,
    },
    {
      field: 'finishDate',
      headerName: 'Finish',
      width: 120,
    },
    {
      field: 'contractStatus',
      headerName: 'Status',
      width: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuClick(e, params.row)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <RootDiv>
      <ContentDiv>
        <StyledPaper>
          <HeaderContainer>
            <TitleBar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  size="small"
                  onClick={() => navigate('/benchmark/projects')}
                  sx={{ mr: 1 }}
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Title variant="h6">
                  <Description fontSize="small" />
                  Contracts
                  {project && (
                    <Typography variant="body2" sx={{ ml: 2, fontWeight: 400, color: 'text.secondary' }}>
                      - {project.projectName}
                    </Typography>
                  )}
                </Title>
              </Box>
              <ButtonGroup>
                <Tooltip title="Add New Contract">
                  <IconButton 
                    color="primary" 
                    onClick={handleAdd} 
                    size="small"
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Selected Contract">
                  <IconButton 
                    color="primary" 
                    onClick={handleEdit} 
                    disabled={!selectedRow} 
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </ButtonGroup>
            </TitleBar>
            {error && (
              <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </HeaderContainer>
          
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {loading && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', zIndex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <StyledDataGrid
              rows={rows}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              onRowClick={handleRowClick}
              disableRowSelectionOnClick
              density="compact"
              autoHeight={false}
              headerHeight={48}
            />
          </Box>
        </StyledPaper>
      </ContentDiv>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuItemClick('Estimate / BoQ')}>
          <ListItemText>Estimate / BoQ</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Actual Cost')}>
          <ListItemText>Actual Cost</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('Change Orders')}>
          <ListItemText>Change Orders</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'background.paper' }}>
          {editMode ? 'Edit Contract' : 'Add New Contract'}
        </DialogTitle>
        <DialogContent dividers sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contract Number"
                name="contractNumber"
                value={formData.contractNumber}
                onChange={handleInputChange}
                required
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contract Name"
                name="contractName"
                value={formData.contractName}
                onChange={handleInputChange}
                required
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" margin="dense">
                <InputLabel>Contract Type</InputLabel>
                <Select
                  name="contractType"
                  value={formData.contractType}
                  onChange={handleInputChange}
                  label="Contract Type"
                >
                  <MenuItem value="Lump Sum">Lump Sum</MenuItem>
                  <MenuItem value="Cost Plus">Cost Plus</MenuItem>
                  <MenuItem value="GM">GM</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Client / Contractor"
                name="clientContractor"
                value={formData.clientContractor}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Original Contract Value"
                name="originalContractValue"
                value={formData.originalContractValue}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Approved Change Value"
                name="approvedChangeValue"
                value={formData.approvedChangeValue}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Revised Contract Value"
                name="revisedContractValue"
                value={formData.revisedContractValue}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Currency Code"
                name="currencyCode"
                value={formData.currencyCode}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contract Status"
                name="contractStatus"
                value={formData.contractStatus}
                onChange={handleInputChange}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Finish Date"
                name="finishDate"
                type="date"
                value={formData.finishDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </RootDiv>
  );
};

export default BenchmarkContracts;
